/**
 * Puerta de sesión.
 *
 * Sin esto, cambiar `mockAdapter` por `httpAdapter` deja la aplicación
 * mostrando «sesión expirada» en todas las vistas: no hay forma de iniciar
 * sesión desde la interfaz, y el único camino sería un `curl`. Conectar la
 * vista al servidor exige que exista la puerta.
 *
 * Es deliberadamente mínima —un formulario y un botón de salida— y está
 * aislada en su propio archivo para que quitarla o reemplazarla no toque nada
 * más. La contraseña sólo existe dentro del estado de este componente y viaja
 * una vez a `/api/auth/login`; la sesión vuelve en cookies HttpOnly que este
 * código no puede leer.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { login, logout, me, pedirRecuperacion, fijarContrasena, type Sesion } from './adapters/http.ts';

type Estado =
  | { readonly k: 'verificando' }
  | { readonly k: 'anonimo'; readonly error: string | null }
  | { readonly k: 'dentro'; readonly sesion: Sesion };

export function SessionGate({ children }: { children: (s: Sesion, salir: () => void) => ReactNode }) {
  const [estado, setEstado] = useState<Estado>({ k: 'verificando' });

  // Al arrancar se pregunta al servidor, no al navegador: la cookie es HttpOnly
  // y este código no puede inspeccionarla ni debería querer hacerlo.
  useEffect(() => {
    void me().then((s) => setEstado(s ? { k: 'dentro', sesion: s } : { k: 'anonimo', error: null }));
  }, []);

  if (estado.k === 'verificando') {
    return (
      <div className="login">
        <p className="notice" role="status" aria-live="polite">Verificando sesión…</p>
      </div>
    );
  }

  if (estado.k === 'anonimo') {
    return <LoginForm error={estado.error} onEntrar={(s) => setEstado({ k: 'dentro', sesion: s })} />;
  }

  // La barra de sesión vive en el header del shell, no flotando arriba de todo.
  return children(
    estado.sesion,
    () => { void logout().then(() => setEstado({ k: 'anonimo', error: null })); },
  );
}

/**
 * Lee el código de recuperación que Supabase deja en el FRAGMENTO de la URL.
 *
 * 🔴 Acá estaba el agujero: Supabase manda `#access_token=…&type=recovery` y no
 * existía nada que lo leyera, así que el enlace del correo parecía inválido. No
 * estaba vencido: no había pantalla del otro lado. Tres personas quedaron sin
 * poder entrar por esto.
 *
 * El fragmento NUNCA viaja al servidor en la petición, así que leerlo es cosa
 * del navegador; el canje lo hace después una función, porque la SPA no habla
 * con Supabase (ADR-010).
 */
export function tokenDeRecuperacion(hash: string): string | null {
  const p = new URLSearchParams(hash.replace(/^#/, ''));
  return p.get('type') === 'recovery' ? p.get('access_token') : null;
}

type Modo = 'entrar' | 'pedir' | 'fijar';

function LoginForm({ error, onEntrar }: { error: string | null; onEntrar: (s: Sesion) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [repetida, setRepetida] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [msg, setMsg] = useState<string | null>(error);
  const [aviso, setAviso] = useState<string | null>(null);
  const [modo, setModo] = useState<Modo>('entrar');
  const [token, setToken] = useState<string | null>(null);

  // Si se llegó desde el correo, se entra directo a fijar la contraseña.
  useEffect(() => {
    const t = tokenDeRecuperacion(window.location.hash);
    if (!t) return;
    setToken(t);
    setModo('fijar');
    // El código sale de la barra de direcciones enseguida: no tiene por qué
    // quedar en el historial ni copiarse sin querer al compartir el enlace.
    window.history.replaceState(null, '', window.location.pathname);
  }, []);

  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    setEnviando(true);
    setMsg(null);
    const r = await login(email, password);
    setEnviando(false);
    // La contraseña deja de existir apenas se usó.
    setPassword('');
    if (typeof r === 'string') setMsg(r);
    else onEntrar(r);
  }

  async function pedirEnlace(e: React.FormEvent) {
    e.preventDefault();
    setEnviando(true); setMsg(null);
    const m = await pedirRecuperacion(email);
    setEnviando(false);
    setAviso(m);
    setModo('entrar');
  }

  async function guardarClave(e: React.FormEvent) {
    e.preventDefault();
    if (password !== repetida) { setMsg('las dos contraseñas no coinciden'); return; }
    setEnviando(true); setMsg(null);
    const err = await fijarContrasena(token ?? '', password);
    setEnviando(false);
    setPassword(''); setRepetida('');
    if (err) { setMsg(err); return; }
    setToken(null);
    setModo('entrar');
    setAviso('Listo. Ya podés entrar con tu contraseña nueva.');
  }

  return (
    <div className="login">
      <section className="cover">
        <div className="cover__beam" aria-hidden="true" />
        <div className="cover__mark" aria-hidden="true">NX</div>

        <div className="cover__top">
          <img className="cover__logo" src="/logo-tops.png" alt="Logística TOPS" />
          <div className="cover__org">
            <b>Logística TOPS</b>
            <span>Verotin S.A.</span>
            <span>Agustín Magaldi 1765 · CABA</span>
            <span>15.000 m² · ANMAT · Audit-ready</span>
          </div>
        </div>

        <div className="cover__body">
          <div>
            <div className="cover__eyebrow">
              <span className="cover__num">01</span>
              <span className="cover__kicker">Acceso · Modelo 2026</span>
              <span className="cover__dash" />
            </div>

            <h1>
              <span className="cover__thin">Nexus</span>
              <span className="cover__bold">Creative Platform</span>
              <span className="cover__red">
                Bienvenidos a la plataforma<br />creativa.
              </span>
            </h1>

            <p className="cover__quote">
              «From creative intent to production-ready deliverables through a governed
              creative platform.»
            </p>
          </div>

          <div className="cover__form">
            <h2>
              {modo === 'entrar' ? 'Ingresar'
                : modo === 'pedir' ? 'Recuperar el acceso'
                : 'Elegí tu contraseña'}
            </h2>
            <p>
              {modo === 'entrar' ? 'Sólo usuarios autorizados. La sesión queda registrada.'
                : modo === 'pedir' ? 'Poné tu correo y te mandamos un enlace para elegir una contraseña nueva.'
                : 'Escribila dos veces. Tiene que tener al menos ocho caracteres.'}
            </p>

            {modo === 'entrar' && (
              <form className="form" onSubmit={(e) => void enviar(e)}>
                <label htmlFor="email">
                  Correo
                  <input
                    id="email" type="email" required autoComplete="username"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                  />
                </label>

                <label htmlFor="password">
                  Contraseña
                  <input
                    id="password" type="password" required autoComplete="current-password"
                    value={password} onChange={(e) => setPassword(e.target.value)}
                  />
                </label>

                <button type="submit" disabled={enviando}>
                  {enviando ? 'Verificando…' : 'Entrar'}
                </button>

                <button type="button" className="enlace"
                        onClick={() => { setModo('pedir'); setMsg(null); setAviso(null); }}>
                  No recuerdo mi contraseña
                </button>
              </form>
            )}

            {modo === 'pedir' && (
              <form className="form" onSubmit={(e) => void pedirEnlace(e)}>
                <label htmlFor="email-rec">
                  Correo
                  <input
                    id="email-rec" type="email" required autoComplete="username"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                  />
                </label>

                <button type="submit" disabled={enviando}>
                  {enviando ? 'Enviando…' : 'Mandame el enlace'}
                </button>

                <button type="button" className="enlace" onClick={() => setModo('entrar')}>
                  Volver
                </button>
              </form>
            )}

            {modo === 'fijar' && (
              <form className="form" onSubmit={(e) => void guardarClave(e)}>
                <label htmlFor="clave-nueva">
                  Contraseña nueva
                  <input
                    id="clave-nueva" type="password" required minLength={8}
                    autoComplete="new-password"
                    value={password} onChange={(e) => setPassword(e.target.value)}
                  />
                </label>

                <label htmlFor="clave-repetida">
                  Repetila
                  <input
                    id="clave-repetida" type="password" required minLength={8}
                    autoComplete="new-password"
                    value={repetida} onChange={(e) => setRepetida(e.target.value)}
                  />
                </label>

                <button type="submit" disabled={enviando}>
                  {enviando ? 'Guardando…' : 'Guardar y entrar'}
                </button>
              </form>
            )}

            {msg && <p className="notice notice--error" role="alert">{msg}</p>}
            {aviso && <p className="notice" role="status">{aviso}</p>}
          </div>
        </div>

        <div className="cover__foot">
          <dl className="cover__meta">
            <div><dt>Sistema</dt><dd>Nexus Creative Platform</dd></div>
            <div><dt>Versión</dt><dd className="mono">1.0</dd></div>
            <div><dt>Entorno</dt><dd className="mono">creative</dd></div>
            <div><dt>Acceso</dt><dd>Restringido</dd></div>
          </dl>
          <p className="cover__conf">
            <b>Confidencial</b>
            Sistema institucional · Uso interno
          </p>
        </div>
      </section>
    </div>
  );
}
